/**
 * Banque 3D : modèles prêts à l'emploi que l'enseignant cherche, prévisualise et ajoute à une leçon.
 * Les modèles ne sont pas relus un par un (c'est la leçon qui l'est) ; seule la licence est contrôlée
 * à l'entrée : usage commercial permis, crédit enregistré.
 */
import { z } from "zod";

export const BANK_SUBJECTS = {
  SVT: "SVT",
  PC: "Physique-chimie",
  GEO: "Géographie",
  MATHS: "Mathématiques",
} as const;
export type BankSubject = keyof typeof BANK_SUBJECTS;

export const BANK_LEVELS = ["6e", "5e", "4e", "3e", "2nde", "1re", "Tle"] as const;
export type BankLevel = (typeof BANK_LEVELS)[number];

/** Thèmes par matière : de quoi filtrer la galerie sans liste à rallonge. */
export const BANK_TOPICS: Record<BankSubject, Record<string, string>> = {
  SVT: {
    anatomie: "Corps humain", nerveux: "Système nerveux", cellule: "Cellule", genetique: "ADN et hérédité",
    immunite: "Immunité", microbes: "Microbes", vegetaux: "Végétaux", animaux: "Animaux",
    ecologie: "Écologie", geologie: "Géologie", fossiles: "Fossiles",
  },
  PC: { molecules: "Molécules", electricite: "Électricité", optique: "Optique", mecanique: "Mécanique", astronomie: "Astronomie" },
  GEO: { relief: "Relief", globe: "Globe et climat", cartes: "Cartes" },
  MATHS: { solides: "Solides", espace: "Géométrie dans l'espace" },
};
export const ALL_TOPICS = Object.assign({}, ...Object.values(BANK_TOPICS)) as Record<string, string>;

/** Licences acceptées dans la banque : toutes permettent l'usage commercial (NC et ND exclues). */
export const BANK_LICENSES = {
  CC0: "CC0 (domaine public)",
  "PUBLIC-DOMAIN": "Domaine public",
  "CC-BY-4.0": "CC BY 4.0",
  "CC-BY-SA-4.0": "CC BY-SA 4.0",
  PROPRIETARY: "Propriété Jang-e",
} as const;
export type BankLicense = keyof typeof BANK_LICENSES;

/** Classement d'un modèle (borné : la galerie reste lisible et l'index petit). */
export const BankTags = z.object({
  subjects: z.array(z.enum(Object.keys(BANK_SUBJECTS) as [BankSubject, ...BankSubject[]])).max(4).default([]),
  levels: z.array(z.enum(BANK_LEVELS)).max(BANK_LEVELS.length).default([]),
  topics: z.array(z.string().refine((t) => t in ALL_TOPICS, "Thème inconnu")).max(6).default([]),
  keywords: z.array(z.string().trim().toLowerCase().min(2).max(40)).max(20).default([]),
  description: z.string().trim().max(600).nullable().default(null),
});
export type BankTags = z.infer<typeof BankTags>;

const Color = z.string().regex(/^#[0-9a-fA-F]{6}$/);

/**
 * Où trouver le fichier et comment le convertir. Un modèle peut réunir plusieurs fichiers
 * (un organe = plusieurs pièces dans BodyParts3D) : chaque pièce devient une partie nommée.
 */
export const BankSource = z.discriminatedUnion("format", [
  z.object({ format: z.literal("glb"), url: z.string().url() }),
  /** Modèle Sketchfab (identifiant de 32 caractères) : licence relue sur l'API à l'import, téléchargement avec la clé du compte. */
  z.object({ format: z.literal("sketchfab"), uid: z.string().regex(/^[0-9a-f]{32}$/) }),
  z.object({ format: z.literal("stl"), url: z.string().url(), color: Color.optional(), upAxis: z.enum(["y", "z"]).default("z") }),
  z.object({
    format: z.literal("obj"), url: z.string().url(), upAxis: z.enum(["y", "z"]).default("y"),
    /** Couleur par partie (nom de groupe « o »/« g » de l'OBJ) ; sinon palette par défaut. */
    colors: z.record(Color).default({}),
  }),
  z.object({
    format: z.literal("pdb"), url: z.string().url(),
    /** Chaînes à garder (vide = toutes) ; l'eau et les ions sont toujours retirés. */
    chains: z.array(z.string().max(4)).max(26).default([]),
    /** Nom affiché par chaîne (« Chaîne α », « Brin 1 »…) ; les chaînes qui partagent un nom forment une seule partie. */
    chainLabels: z.record(z.string().min(1).max(40)).default({}),
  }),
  z.object({
    /** Archive de BodyParts3D : pièces (FJxxxx.obj) réunies d'après la liste des éléments de chaque concept FMA. */
    format: z.literal("bodyparts3d"), zipUrl: z.string().url(), elementsUrl: z.string().url(),
    /** Chaque partie : un concept FMA (toutes ses pièces) ou une liste précise de pièces FJ (découpage fin). */
    parts: z.array(z.object({
      fma: z.string().regex(/^FMA\d+$/).optional(),
      fj: z.array(z.string().regex(/^FJ\d+M?$/)).min(1).max(400).optional(),
      name: z.string().min(1).max(60), color: Color,
    }).refine((p) => !!p.fma !== !!p.fj, "fma ou fj (l'un des deux)")).min(1).max(30),
  }),
]);
export type BankSource = z.infer<typeof BankSource>;

export const BankManifestEntry = BankTags.extend({
  slug: z.string().regex(/^[a-z0-9-]{3,80}$/),
  title: z.string().trim().min(2).max(160),
  source: BankSource,
  /** Page publique du modèle (pour vérifier la licence plus tard). */
  pageUrl: z.string().url(),
  license: z.enum(Object.keys(BANK_LICENSES) as [BankLicense, ...BankLicense[]]),
  author: z.string().min(2).max(120),
  attribution: z.string().min(2).max(300),
  /** Triangles visés après allègement (le contrôle général reste MODEL_LIMITS). */
  maxTriangles: z.number().int().min(1_000).max(100_000).default(40_000),
});
export type BankManifestEntry = z.infer<typeof BankManifestEntry>;

export const BankManifest = z.object({ version: z.literal(1), entries: z.array(BankManifestEntry).max(2_000) }).superRefine((m, ctx) => {
  const seen = new Set<string>();
  m.entries.forEach((e, i) => { if (seen.has(e.slug)) ctx.addIssue({ code: "custom", path: ["entries", i, "slug"], message: `slug en double : ${e.slug}` }); seen.add(e.slug); });
});
export type BankManifest = z.infer<typeof BankManifest>;

/** Crédit court affiché sous le modèle dans l'app (obligatoire pour CC BY et CC BY-SA). */
export const creditLine = (a: { title: string; author: string | null; license: string; attribution: string | null }) =>
  a.license === "PROPRIETARY" ? null : (a.attribution ?? `${a.title} · ${a.author ?? "auteur inconnu"} · ${BANK_LICENSES[a.license as BankLicense] ?? a.license}`);

/** Recherche sans accents ni majuscules : « coeur » trouve « Cœur ». */
export const foldSearch = (s: string) => s.normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/œ/g, "oe").replace(/Œ/g, "oe").replace(/æ/g, "ae").toLowerCase().trim();
export const searchTextOf = (a: { title: string; description?: string | null; keywords?: string[] }) =>
  foldSearch([a.title, a.description ?? "", ...(a.keywords ?? [])].join(" ")).replace(/\s+/g, " ").slice(0, 2000);
